import { memo, useEffect, useRef, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { ButtonSpinner } from '../../components/Spinner';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { navigate } from '../../lib/router';
import { useSite } from '../../lib/site';
import { useAuth } from '../../lib/auth';
import { Interview } from './Interview';

type State = 'form' | 'progress' | 'complete' | 'failed';
const MAX_PROMPT = 5000;

interface BuildStep { name: string; status: string; }

/** Fallback rotation when the API hasn't reported a step yet (first ~5s of a
 * build). Once `currentStep` arrives we stop cycling and show the real text. */
const ROTATING_FALLBACK = [
  'Analyzing your request…',
  'Spinning up the build container…',
  'Waiting on the agent to report in…',
];

/** Maps the raw step names the API uses to human-friendly labels for the
 * checklist. Anything missing falls back to the underscored name title-cased. */
const STEP_LABEL: Record<string, string> = {
  concept: 'Understanding your concept',
  research: 'Researching the business',
  content: 'Writing content',
  visual: 'Planning visuals',
  design: 'Building design system',
  style_guide: 'Generating style guide',
  image_prompts: 'Planning imagery',
  hero_image: 'Generating hero image',
  about_image: 'Generating about image',
  images: 'Generating images',
  html: 'Building HTML & CSS',
  edit: 'Applying your changes',
  upload: 'Publishing preview',
};
function stepLabel(name: string): string {
  return STEP_LABEL[name] || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function mainDomain(): string {
  const h = window.location.hostname;
  return h.endsWith('webforcheap.com') ? 'webforcheap.com' : 'appsforcheap.com';
}

/** Per-step row in the live checklist on the progress card. Icon + label adapt
 * to the API-reported status (pending / running / complete / failed). */
function StepRow({ step }: { step: BuildStep }) {
  const s = step.status;
  const label = stepLabel(step.name);
  let icon, textCls;
  if (s === 'complete') {
    icon = <svg class="w-3.5 h-3.5 text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>;
    textCls = 'text-slate-400';
  } else if (s === 'running') {
    icon = <div class="w-3.5 h-3.5 border-2 border-slate-700 border-t-cyan-400 rounded-full animate-spin shrink-0"></div>;
    textCls = 'text-cyan-400 font-medium';
  } else if (s === 'failed' || s === 'error') {
    icon = <svg class="w-3.5 h-3.5 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>;
    textCls = 'text-red-400';
  } else {
    // pending / unknown
    icon = <div class="w-3.5 h-3.5 rounded-full border border-slate-600 shrink-0"></div>;
    textCls = 'text-slate-500';
  }
  return <li class={`flex items-center gap-2 text-xs ${textCls}`}>{icon}<span>{label}</span></li>;
}

interface EditorColumnProps {
  state: State;
  revision: boolean;
  progress: number;
  liveMessage: string;
  steps: BuildStep[];
  failedMsg: string;
  site: any;
  siteId: string;
  hasPreview: boolean;
  hasLive: boolean;
  canPublish: boolean;
  publishReason: string;
  publishing: boolean;
  notifyEmail: boolean;
  notifyAddress: string;
  onBuild: (prompt: string, fromBrief?: boolean) => void;
  publishSite: () => void;
  onMakeAnother: () => void;
  onTryAgain: () => void;
}

/**
 * Left editor column. **Owns the `prompt` state itself** — this is deliberate
 * and load-bearing: keeping the textarea's per-keystroke state down here means
 * typing re-renders only this component, never the parent `SiteBuild`. That
 * matters because `SiteBuild` also renders the preview `<iframe>`; when the
 * parent re-renders, hono/jsx/dom's reconciler re-inserts the iframe element
 * into the DOM, and re-inserting an iframe forces the browser to RELOAD it.
 * (The reload was visible only on a hard/deep page load — the mount path that
 * leaves the reconciler computing a stale insert position. Isolating the state
 * here removes the parent re-render entirely, so it can't happen at all.)
 */
function EditorColumn({
  state, revision, progress, liveMessage, steps, failedMsg,
  site, siteId, hasPreview, hasLive, canPublish, publishReason, publishing,
  notifyEmail, notifyAddress,
  onBuild, publishSite, onMakeAnother, onTryAgain,
}: EditorColumnProps) {
  const [prompt, setPrompt] = useState<string>('');
  const [describe, setDescribe] = useState<boolean>(false);
  const promptLen = prompt.length;
  const interview = state === 'form' && !revision && !describe;
  const promptCounterAmber = promptLen > MAX_PROMPT * 0.9;

  return (
    <div class="flex flex-col">
      <div class="flex items-center gap-2 mb-2 min-h-[32px]">
        <span class="text-xs font-medium text-slate-400">Editor</span>
        {state === 'form' && !interview && (
          <span class={'text-xs font-mono ' + (promptCounterAmber ? 'text-amber-400' : 'text-slate-500')}>
            {promptLen} / {MAX_PROMPT}
          </span>
        )}
        <div class="ml-auto flex items-center gap-1">
          {state === 'form' && prompt.length > 0 && (
            <button type="button" onClick={() => setPrompt('')} title="Clear prompt" class="p-2 text-slate-400 hover:text-slate-200 hover:bg-dark-700 rounded-md transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            </button>
          )}
        </div>
      </div>

      {interview && (
        <Interview site={site} siteId={siteId} onBuild={() => onBuild('', true)} onSkip={() => setDescribe(true)} />
      )}

      {state === 'form' && !interview && (
        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
          <h2 class="text-lg font-semibold text-slate-100">{revision ? 'Update Your Site' : 'Build with Spark'}</h2>
          <p class="text-sm text-slate-400 mt-0.5 mb-5">{revision ? 'Describe the changes you want to make' : 'AI-powered website in minutes'}</p>
          <form onSubmit={(e: Event) => { e.preventDefault(); onBuild(prompt); }} class="space-y-5">
            <div>
              <label for="sparkPrompt" class="block text-sm font-medium text-slate-300 mb-1.5">
                {revision ? 'What would you like to change? *' : 'Describe your site *'}
              </label>
              <textarea
                id="sparkPrompt" required rows={12} minLength={10} maxLength={MAX_PROMPT}
                value={prompt}
                onInput={(e: Event) => setPrompt((e.currentTarget as HTMLTextAreaElement).value)}
                placeholder={revision ? 'Describe what you want to update…' : 'Tell us about your project. For example: We are starting a yoga studio in Austin called Sunrise Flow…'}
                class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              />
              {revision ? (
                <div class="text-xs text-slate-500 mt-1">
                  Examples:
                  <ul class="list-disc pl-4 mt-1 space-y-0.5">
                    <li>Change the phone number to 555-1234</li>
                    <li>Update the hero headline to Welcome Home</li>
                    <li>Add a testimonial from John Smith</li>
                    <li>Update the logo everywhere using the logo.png in materials</li>
                  </ul>
                </div>
              ) : (
                <p class="text-xs text-slate-500 mt-1">Include the name, what you do, your target audience, and the feel you're going for. Our AI will figure out the rest.</p>
              )}
            </div>
            {!revision && (
              <div class="p-3 rounded-lg border border-slate-600/50 bg-dark-700/40 text-xs text-slate-400">
                Heads up: the first build takes a while. You do not have to wait on this page
                {notifyEmail
                  ? <>, we'll email {notifyAddress || 'you'} as soon as your site is ready.</>
                  : <>. Build emails are currently off, so check back here for the result.</>}
              </div>
            )}
            {site && !site.email && (
              <div class="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-300">
                No contact email set for this site. <a href={`/sites/${siteId}/settings`} class="underline hover:text-amber-200">Add one in Settings</a> before building.
              </div>
            )}
            <div class="flex items-center justify-between pt-4 border-t border-slate-700/50">
              <a
                href={`https://${mainDomain()}/contact.html`} target="_blank" rel="noopener"
                class="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1.5"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Request support
              </a>
              <button type="submit" class="px-6 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={revision ? 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' : 'M13 10V3L4 14h7v7l9-11h-7z'}/>
                </svg>
                {revision ? 'Apply Changes' : 'Build My Site'}
              </button>
            </div>
          </form>
        </div>
      )}

      {state === 'progress' && (
        <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-8">
          <div class="text-center mb-6">
            <h2 class="text-lg font-medium text-slate-100">{revision ? 'Applying your changes…' : 'Building your site…'}</h2>
            <p class="text-sm text-slate-400 mt-1">
              {revision
                ? 'This usually takes 1-5 minutes. Image generation takes longer.'
                : 'The first build is the slow one. Everything is written and generated from scratch, so give it a while.'}
            </p>
          </div>
          <div class="h-2 bg-slate-700 rounded-full overflow-hidden relative">
            <div
              class="h-full bg-gradient-to-r from-cyan-500/50 via-cyan-400 to-cyan-500/50 rounded-full transition-all duration-300 animate-shimmer animate-pulse-glow"
              style={{ width: progress + '%' }}
            ></div>
          </div>
          <p class="text-sm text-cyan-400 mt-4 text-center">{liveMessage}</p>
          {steps.length > 0 && (
            <ul class="mt-5 space-y-1.5 max-w-sm mx-auto">
              {steps.map((s) => <StepRow step={s} />)}
            </ul>
          )}
          <p class="text-xs text-slate-500 mt-4 text-center">
            You can safely leave this page. The build keeps running in the background
            {notifyEmail
              ? <> and we'll email {notifyAddress ? <span class="text-slate-400">{notifyAddress}</span> : 'you'} when it's done. </>
              : <>. Build emails are off for your account. </>}
            <a
              href="/account"
              onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/account'); }}
              class="underline hover:text-slate-300"
            >{notifyEmail ? 'Manage email notifications' : 'Turn them on'}</a>
          </p>
        </div>
      )}

      {state === 'complete' && (
        <div class="bg-dark-800 border border-emerald-500/30 rounded-xl p-6 text-center">
          <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
          </div>
          <h2 class="text-lg font-semibold text-slate-100 mb-1">{revision ? 'Changes applied!' : 'Your site is ready!'}</h2>
          <p class="text-slate-400 text-sm mb-4">Preview is on the right — Publish below when ready.</p>
          <button
            type="button"
            onClick={() => { setPrompt(''); onMakeAnother(); }}
            class="inline-flex items-center gap-2 border border-slate-600 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-dark-700 transition-colors"
          >Make Another Update</button>
        </div>
      )}

      {state === 'failed' && (
        <div class="bg-dark-800 border border-red-500/30 rounded-xl p-6 text-center">
          <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
          </div>
          <h2 class="text-lg font-semibold text-slate-100 mb-1">Build Failed</h2>
          <p class="text-slate-400 text-sm mb-4">{failedMsg}</p>
          <button type="button" onClick={onTryAgain} class="inline-flex items-center gap-2 bg-cyan-500 text-dark-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors">
            Try Again
          </button>
        </div>
      )}

      {/* Action group — pinned to the bottom of the column on lg so it
          aligns with the iframe's bottom edge. */}
      <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-4 mt-4 lg:mt-auto">
        <div class="grid grid-cols-3 gap-2">
          <a
            href={hasPreview ? site!.previewUrl : '#'} target="_blank" rel="noopener"
            title={hasPreview ? 'Open the latest preview in a new tab' : 'Build the site to see a preview'}
            class={`flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm font-medium text-slate-200 hover:bg-dark-600 transition-colors ${!hasPreview ? 'pointer-events-none opacity-40' : ''}`}
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
            </svg>
            View preview
          </a>
          <button
            type="button" onClick={publishSite} disabled={!canPublish || publishing}
            title={publishReason}
            class="flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-500 text-dark-900 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-cyan-500"
          >
            {publishing ? <><ButtonSpinner /> Publishing…</> : <>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/></svg>
              Publish
            </>}
          </button>
          <a
            href={hasLive ? site!.url : '#'} target="_blank" rel="noopener"
            title={hasLive ? 'Open the live site in a new tab' : 'Site has not been published yet'}
            class={`flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm font-medium text-slate-200 hover:bg-dark-600 transition-colors ${!hasLive ? 'pointer-events-none opacity-40' : ''}`}
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
            View live
          </a>
        </div>
      </div>
    </div>
  );
}

interface PreviewPaneProps {
  site: any;
  hasPreview: boolean;
  /** The running build has already published a page worth watching, so the
   * progress overlay steps aside for a status strip instead of covering it. */
  livePreview: boolean;
  state: State;
  progress: number;
  liveMessage: string;
  iframeRef: { current: HTMLIFrameElement | null };
}

/** The preview iframe, isolated behind its OWN memo boundary + managed
 * imperatively so the vdom never touches the <iframe> element.
 *
 * Two things are load-bearing:
 * 1. memo on STABLE props (previewUrl + hasPreview) — PreviewPane re-renders
 *    every ~2s poll (progress/liveMessage change), but those props never reach
 *    here, so hono SKIPS this subtree and never re-creates the host <div>.
 * 2. Imperative iframe — appended into the host <div> ONCE. Even if a render
 *    did slip through, the parentElement guard prevents re-parenting (which is
 *    what forces the browser to RELOAD an iframe). src changes only when the
 *    URL actually changes.
 * Without (1), hono re-creates the host div on each poll, the guard sees a new
 * parent, re-appends -> reload. That was the flicker. */
const IframeLayer = memo(function IframeLayer({ previewUrl, hasPreview, iframeRef }: { previewUrl: string | undefined; hasPreview: boolean; iframeRef: { current: HTMLIFrameElement | null } }) {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !hasPreview || !previewUrl) return;
    let frame = iframeRef.current;
    if (!frame) {
      frame = document.createElement('iframe');
      frame.className = 'absolute inset-0 w-full h-full bg-white';
      (frame as any).loading = 'lazy';
      iframeRef.current = frame;
    }
    if (frame.parentElement !== host) host.appendChild(frame);
    if (frame.getAttribute('data-url') !== previewUrl) {
      frame.src = previewUrl;
      frame.setAttribute('data-url', previewUrl);
    }
  }, [previewUrl, hasPreview]);
  return <div ref={hostRef} class="absolute inset-0"></div>;
});

/** Right-hand live preview column. Extracted (and memoized) so it is a sibling
 * of the editor rather than interleaved with it; the iframe lives here and is
 * never reconciled while the user types (see EditorColumn for the why). */
const PreviewPane = memo(function PreviewPane({ site, hasPreview, livePreview, state, progress, liveMessage, iframeRef }: PreviewPaneProps) {
  function reloadPreview() {
    const base = site?.previewUrl;
    if (!base || !iframeRef.current) return;
    const sep = base.includes('?') ? '&' : '?';
    iframeRef.current.src = base + sep + '_t=' + Date.now();
  }

  // While the build is still writing pages, re-pull the published preview so the
  // customer sees it fill in rather than one early snapshot.
  useEffect(() => {
    if (!livePreview) return;
    const t = setInterval(reloadPreview, 30000);
    return () => clearInterval(t);
  }, [livePreview, site?.previewUrl]);

  return (
    <div class="flex flex-col">
      <div class="flex items-center gap-2 mb-2 min-h-[32px]">
        <span class="text-xs font-medium text-slate-400">Preview</span>
        {hasPreview && (
          <span class="text-xs text-slate-500 font-mono truncate min-w-0">{site!.previewUrl.replace(/^https?:\/\//, '')}</span>
        )}
        <div class="ml-auto flex items-center gap-1">
          <button
            type="button" onClick={reloadPreview} disabled={!hasPreview}
            title="Refresh preview"
            class="p-2 text-slate-400 hover:text-slate-200 hover:bg-dark-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </button>
          {hasPreview && (
            <a href={site!.previewUrl} target="_blank" rel="noopener" class="p-2 text-slate-400 hover:text-cyan-400 hover:bg-dark-700 rounded-md transition-colors" title="Open preview in new tab">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
            </a>
          )}
        </div>
      </div>
      <div
        class={`relative bg-dark-800 border rounded-xl overflow-hidden transition-colors ${state === 'progress' ? 'border-cyan-500/40' : 'border-slate-700/50'}`}
        style={{ height: 'calc(100vh - 14rem)', minHeight: '560px' }}
      >
        <IframeLayer previewUrl={site?.previewUrl} hasPreview={hasPreview} iframeRef={iframeRef} />
        {!hasPreview && state !== 'progress' && (
          <div class="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
            <div class="w-12 h-12 rounded-full bg-dark-700 flex items-center justify-center mb-3">
              <svg class="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
            </div>
            <p class="text-slate-300 text-sm font-medium">No preview yet</p>
            <p class="text-slate-500 text-xs mt-1">Build your site to see it here.</p>
          </div>
        )}
        {state === 'progress' && !livePreview && (
          <div class="absolute inset-0 flex flex-col items-center justify-center bg-dark-900/80 backdrop-blur-sm z-10 px-6 text-center">
            <div class="w-16 h-16 border-4 border-slate-700 border-t-cyan-400 rounded-full animate-spin mb-5"></div>
            <p class="text-cyan-400 text-sm font-medium">{liveMessage}</p>
            <p class="text-slate-500 text-xs mt-2 font-mono">{Math.round(progress)}% complete</p>
            {hasPreview && (
              <p class="text-slate-600 text-xs mt-4 max-w-xs">Your previous preview is still behind this overlay — it'll update automatically when the build finishes.</p>
            )}
          </div>
        )}
        {state === 'progress' && livePreview && (
          <div class="absolute bottom-0 left-0 right-0 flex items-center gap-3 bg-dark-900/90 backdrop-blur-sm z-10 px-4 py-3 border-t border-cyan-500/30">
            <div class="w-4 h-4 border-2 border-slate-700 border-t-cyan-400 rounded-full animate-spin shrink-0"></div>
            <p class="text-cyan-400 text-xs font-medium truncate">{liveMessage}</p>
            <p class="text-slate-500 text-xs font-mono ml-auto shrink-0">{Math.round(progress)}%</p>
          </div>
        )}
      </div>
    </div>
  );
});

export function SiteBuild({ siteId }: { siteId: string }) {
  const { site, loading: siteLoading, reload: reloadSite } = useSite(siteId);
  const { user } = useAuth();

  const [revision, setRevision] = useState<boolean>(false);
  const [state, setState] = useState<State>('form');
  const [progress, setProgress] = useState<number>(5);
  const [statusIdx, setStatusIdx] = useState<number>(0);
  // Real progress from the API status endpoint. `currentStep` is a human string
  // set by the container; `steps` is the full phase list with per-step status.
  const [currentStep, setCurrentStep] = useState<string>('');
  const [steps, setSteps] = useState<BuildStep[]>([]);
  // The running build publishes its preview as soon as there is a styled page,
  // and the status endpoint answers with previewUrl from that point on. Until
  // then there is nothing to show.
  const [livePreviewReady, setLivePreviewReady] = useState<boolean>(false);
  const [failedMsg, setFailedMsg] = useState<string>('');
  const [lastBuildAt, setLastBuildAt] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<boolean>(false);

  const pollRef = useRef<number | null>(null);
  const rotateRef = useRef<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Once the site loads, derive initial mode + state.
  useEffect(() => {
    if (!site) return;
    const isBuilt = !!(site.buildType && site.buildType.length > 0);
    setRevision(isBuilt);
    if (site.status === 'building') { enterProgress(); pollStatus(); }
    else setState('form');
    loadLastBuildAt();
  }, [site?.id, site?.status]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (rotateRef.current) clearInterval(rotateRef.current);
  }, []);

  async function loadLastBuildAt() {
    try {
      const r = await api('/sites/' + siteId + '/versions?_t=' + Date.now(), { cache: 'no-store' });
      if (!r.ok) return;
      const data = await r.json();
      if (data.versions && data.versions.length) {
        const cur = data.versions.find((v: any) => v.isCurrent) || data.versions[0];
        setLastBuildAt(cur.timestamp);
      }
    } catch {}
  }

  function startRotation() {
    setStatusIdx(0);
    if (rotateRef.current) clearInterval(rotateRef.current);
    rotateRef.current = setInterval(() => setStatusIdx((i) => (i + 1) % ROTATING_FALLBACK.length), 4000) as unknown as number;
  }
  function stopRotation() {
    if (rotateRef.current) { clearInterval(rotateRef.current); rotateRef.current = null; }
  }

  function enterProgress() {
    setState('progress'); setProgress(5); setCurrentStep(''); setSteps([]);
    setLivePreviewReady(false); startRotation();
  }

  /** What to show as the primary status line: prefer the real currentStep from
   * the API; fall back to the rotation while the agent hasn't reported in. */
  const liveMessage = currentStep && currentStep !== 'Unknown' ? currentStep : ROTATING_FALLBACK[statusIdx];

  async function onBuild(prompt: string, fromBrief = false) {
    const contactEmail = (site && site.email) || user?.email || '';
    if (!contactEmail) { setFailedMsg('Set a contact email in Settings before building.'); setState('failed'); return; }
    enterProgress();
    try {
      const r = await api('/sites/' + siteId + '/spark-pro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fromBrief ? { fromBrief: true, contactEmail } : { prompt, contactEmail }),
      });
      const result = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((result as any).error || 'Failed to start build');
      pollStatus();
    } catch (err: any) {
      stopRotation();
      setFailedMsg(err?.message || 'Build failed');
      setState('failed');
    }
  }

  function pollStatus() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api('/sites/' + siteId + '/spark-pro/status');
        if (!r.ok) throw new Error('Failed to get status');
        const status = await r.json();
        if (status.progress) setProgress(status.progress);
        if (status.currentStep) setCurrentStep(status.currentStep);
        if (Array.isArray(status.steps)) setSteps(status.steps);
        if (status.previewUrl) setLivePreviewReady(true);
        if (status.status === 'complete') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          await showComplete(status);
        } else if (status.status === 'failed') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          stopRotation();
          setFailedMsg(status.error || 'Something went wrong. Please try again.');
          setState('failed');
        }
      } catch (e) { console.error('Status poll error:', e); }
    }, 2000) as unknown as number;
  }

  async function showComplete(status: any) {
    stopRotation();
    setState('complete');
    // Reload site to pick up updated buildType/status/publishedAt.
    await reloadSite();
    await loadLastBuildAt();
    const url = status.previewUrl || status.siteUrl || site?.previewUrl;
    if (iframeRef.current && url) {
      const sep = url.includes('?') ? '&' : '?';
      iframeRef.current.src = url + sep + '_t=' + Date.now();
    }
  }

  const isBuilt = !!(site && site.buildType && site.buildType.length > 0);
  // A first build has no buildType until it finishes, so without the live flag
  // the customer watches a blank pane through the whole build.
  const hasPreview = !!((isBuilt || livePreviewReady) && site?.previewUrl);
  const hasLive = !!(site?.publishedAt && site?.url);
  const newerPreview = lastBuildAt && site?.publishedAt
    ? new Date(lastBuildAt).getTime() > new Date(site.publishedAt).getTime()
    : false;
  const canPublish = hasPreview && site?.status !== 'building' && (!site?.publishedAt || newerPreview);
  const publishReason = !hasPreview ? 'Build the site first'
    : site?.status === 'building' ? 'A build is in progress'
    : !site?.publishedAt ? 'Publish the current preview to production'
    : newerPreview ? 'Publish the latest preview to production'
    : 'No new build since the last publish';

  async function publishSite() {
    if (!site || !site.previewUrl) return;
    const isDev = window.location.hostname.includes('appsforcheap');
    let hasDomain = false;
    try {
      const dr = await api('/sites/' + siteId + '/domain');
      const dd = await dr.json();
      hasDomain = !!(dd.configured && dd.domain);
      if (hasDomain && dd.status !== 'active') {
        if (!confirm(`Your domain is not fully verified yet.\n\nStatus: ${dd.statusMessage || 'Pending verification'}\n\nYou can still publish, but your site may not be accessible at your custom domain until DNS is configured.\n\nContinue anyway?`)) {
          navigate(`/sites/${siteId}/domain`); return;
        }
      }
    } catch (e) { console.error('Failed to check domain status:', e); }

    const suffix = hasDomain ? '\n\nThis will make your site live at your custom domain.' : (isDev ? '' : '\n\nThis will publish your site to production.');
    if (!confirm(`Publish "${site.name}" to production?${suffix}`)) return;

    setPublishing(true);
    showToast('info', 'Publishing', `Pushing "${site.name}" to production…`);
    try {
      const resp = await api('/sites/' + siteId + '/promote', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error((d as any).error || 'Failed to promote'); }
      let attempts = 0, done = false, failed = false;
      while (attempts < 60 && !done && !failed) {
        await new Promise((r) => setTimeout(r, 2000)); attempts++;
        try {
          const sr = await api('/sites/' + siteId + '/spark-pro/status');
          if (sr.ok) {
            const s = await sr.json();
            if (s.status === 'complete' || s.status === 'live') { done = true; break; }
            if (s.status === 'failed') { failed = true; break; }
          }
        } catch (e) { console.error('Polling error:', e); }
      }
      await reloadSite(); await loadLastBuildAt();
      if (done) showToast('success', 'Published!', `"${site.name}" is now live.`);
      else if (failed) showToast('error', 'Publish Failed', 'The publish job failed. Please try again.');
      else showToast('info', 'Publishing', 'Taking longer than expected. Check back in a moment.');
    } catch (err: any) {
      showToast('error', 'Publish Failed', err?.message || 'Publish failed');
    } finally { setPublishing(false); }
  }

  return (
    <SiteShell site={site} loading={siteLoading} active="build">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: editor — owns the prompt state so typing never re-renders this
            parent (and thus never reloads the preview iframe). */}
        <EditorColumn
          state={state}
          revision={revision}
          progress={progress}
          liveMessage={liveMessage}
          steps={steps}
          failedMsg={failedMsg}
          site={site}
          siteId={siteId}
          hasPreview={hasPreview}
          hasLive={hasLive}
          canPublish={canPublish}
          publishReason={publishReason}
          publishing={publishing}
          notifyEmail={user?.notifyBuildComplete !== false}
          notifyAddress={user?.email || ''}
          onBuild={onBuild}
          publishSite={publishSite}
          onMakeAnother={() => { setRevision(true); setState('form'); }}
          onTryAgain={() => setState('form')}
        />

        {/* RIGHT: live preview iframe */}
        <PreviewPane
          site={site}
          hasPreview={hasPreview}
          livePreview={livePreviewReady && hasPreview}
          state={state}
          progress={progress}
          liveMessage={liveMessage}
          iframeRef={iframeRef}
        />
      </div>
    </SiteShell>
  );
}
